/**
 * Statuses of a Stripe subscription invoice surfaced in the Publisher Invoices
 * table. A subset of Stripe.Invoice.Status: drafts are not finalized and
 * uncollectible is an internal Stripe state, so the list endpoint filters both
 * out rather than showing them to publishers.
 */
export const PUBLISHER_INVOICE_STATUSES = ['open', 'paid', 'void'] as const;

export type PublisherInvoiceStatus =
  (typeof PUBLISHER_INVOICE_STATUSES)[number];

/**
 * Trimmed, serializable view of a Stripe invoice for the Publisher Invoices
 * table. Only whitelisted scalar fields — never return a raw Stripe.Invoice
 * over the wire.
 */
export type PublisherInvoiceView = {
  /** Stripe invoice id (in_...) */
  id: string;

  /** Display name: invoice number, falling back to description or id */
  name: string;

  /** Creation time in unix seconds (Stripe native) */
  createdAt: number;

  /** Due date in unix seconds; null when Stripe has no due date set */
  dueDate: number | null;

  totalInCents: number;

  amountDueInCents: number;

  /** ISO currency code, e.g. 'usd' */
  currency: string;

  status: PublisherInvoiceStatus;

  /** Stripe-hosted payment page; null when Stripe has not published one */
  hostedInvoiceUrl: string | null;
};

/** Cap on invoices returned by payments/publisher-invoices; mirrors MAX_INVOICES_QUERY_SIZE */
export const MAX_PUBLISHER_INVOICES = 1000;

/**
 * Cap on invoices per payments/publisher-invoices/download request. Each
 * invoice costs a Stripe PDF fetch plus CSV generation, so bulk downloads are
 * bounded to keep the request within function limits.
 */
export const MAX_PUBLISHER_INVOICE_DOWNLOADS = 24;
